import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import toast from 'react-hot-toast';
import { AuthContext } from '../../context/AuthContext';
import { 
    UserGroupIcon, 
    AcademicCapIcon, 
    ClipboardDocumentCheckIcon,
    ChartBarIcon,
    PlusCircleIcon,
    ArrowTrendingUpIcon,
    EnvelopeIcon
} from '@heroicons/react/24/outline';

const AdminDashboardPage = () => {
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalCourses: 0,
        totalQuizzes: 0,
        totalEnrollments: 0,
        totalContacts: 0,
        unreadContacts: 0,
        newUsers: 0
    });
    const [loading, setLoading] = useState(true);
    const { token } = useContext(AuthContext);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const config = { headers: { 'x-auth-token': token } };
                const res = await axios.get(`${API_BASE_URL}/api/admin/stats`, config);
                setStats(res.data);
            } catch (err) {
                console.error('Could not load statistics:', err);
                toast.error('Failed to load statistics');
            } finally {
                setLoading(false);
            }
        };
        if (token) fetchStats();
    }, [token]);

    const cardVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
    };

    const statCards = [
        {
            title: 'Total Users',
            value: stats.totalUsers,
            icon: UserGroupIcon,
            color: 'from-blue-500 to-cyan-500',
            link: '/admin/users',
            badge: stats.newUsers > 0 ? `+${stats.newUsers}` : null,
            badgeColor: 'bg-green-500'
        },
        {
            title: 'Total Courses',
            value: stats.totalCourses,
            icon: AcademicCapIcon,
            color: 'from-purple-500 to-pink-500',
            link: '/admin/courses'
        },
        {
            title: 'Total Quizzes',
            value: stats.totalQuizzes,
            icon: ClipboardDocumentCheckIcon,
            color: 'from-orange-500 to-red-500',
            link: '/admin/quizzes'
        },
        {
            title: 'Enrollments',
            value: stats.totalEnrollments,
            icon: ArrowTrendingUpIcon,
            color: 'from-green-500 to-emerald-500',
            link: '#'
        },
        {
            title: 'Messages',
            value: stats.totalContacts,
            icon: EnvelopeIcon,
            color: 'from-yellow-500 to-orange-500',
            link: '/admin/contacts',
            badge: stats.unreadContacts > 0 ? stats.unreadContacts : null,
            badgeColor: 'bg-red-500 animate-pulse'
        }
    ];

    const quickActions = [
        {
            title: 'Add Course',
            description: 'Create new training course',
            link: '/admin/courses/add',
            icon: AcademicCapIcon,
            color: 'bg-blue-500'
        },
        {
            title: 'Create Quiz',
            description: 'Prepare quiz questions',
            link: '/admin/quizzes/add',
            icon: ClipboardDocumentCheckIcon,
            color: 'bg-purple-500'
        },
        {
            title: 'Manage Users',
            description: 'Edit roles and permissions',
            link: '/admin/users',
            icon: UserGroupIcon,
            color: 'bg-green-500'
        },
        {
            title: 'View Messages',
            description: 'Check user inquiries',
            link: '/admin/contacts',
            icon: EnvelopeIcon,
            color: 'bg-yellow-500'
        }
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
            >
                <h1 className="text-3xl md:text-4xl font-bold mb-1">Admin Dashboard</h1>
                <p className="text-sm opacity-70">Monitor and manage your platform</p>
            </motion.div>

            {/* Stats Grid */}
            <motion.div
                initial="hidden"
                animate="visible"
                variants={{
                    visible: {
                        transition: {
                            staggerChildren: 0.1
                        }
                    }
                }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            >
                {statCards.map((stat, index) => (
                    <motion.div
                        key={index}
                        variants={cardVariants}
                        whileHover={{ scale: 1.03, y: -3 }}
                        transition={{ type: 'spring', stiffness: 300 }}
                    >
                        <Link to={stat.link} className="card p-4 block h-full relative overflow-hidden group">
                            {/* Background Gradient */}
                            <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />
                            
                            <div className="relative z-10">
                                <div className="flex items-start justify-between mb-3">
                                    <div className={`p-2 rounded-lg bg-gradient-to-br ${stat.color}`}>
                                        <stat.icon className="h-5 w-5 text-white" />
                                    </div>
                                    {stat.badge && (
                                        <span className={`text-xs ${stat.badgeColor} text-white px-2 py-1 rounded-full font-semibold`}>
                                            {stat.badge}
                                        </span>
                                    )}
                                </div>
                                
                                <h3 className="text-xs font-medium opacity-70 mb-1">{stat.title}</h3>
                                <p className="text-2xl font-bold">{loading ? '...' : stat.value}</p>
                            </div>
                        </Link>
                    </motion.div>
                ))}
            </motion.div>

            {/* Quick Actions */}
            <div className="card p-5">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <PlusCircleIcon className="h-6 w-6 text-light-primary dark:text-dark-primary" />
                    Quick Actions
                </h2>
                
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={{
                        visible: {
                            transition: {
                                staggerChildren: 0.1
                            }
                        }
                    }}
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
                >
                    {quickActions.map((action, index) => (
                        <motion.div
                            key={index}
                            variants={cardVariants}
                            whileHover={{ scale: 1.02 }}
                        >
                            <Link
                                to={action.link}
                                className="block p-4 rounded-lg border-2 border-light-border dark:border-dark-border hover:border-light-primary dark:hover:border-dark-primary transition-all bg-light-bg dark:bg-dark-bg"
                            >
                                <div className={`${action.color} w-10 h-10 rounded-lg flex items-center justify-center mb-3`}>
                                    <action.icon className="h-5 w-5 text-white" />
                                </div>
                                <h3 className="text-sm font-semibold mb-1">{action.title}</h3>
                                <p className="text-xs opacity-70">{action.description}</p>
                            </Link>
                        </motion.div>
                    ))}
                </motion.div>
            </div>

            {/* Management Links */}
            <div className="card p-5">
                <h2 className="text-xl font-bold mb-4">Management Tools</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    <Link to="/admin/courses" className="p-3 rounded-lg bg-light-bg dark:bg-dark-bg hover:bg-light-border dark:hover:bg-dark-border transition-colors flex items-center justify-between group">
                        <div>
                            <h3 className="font-semibold text-sm mb-0.5">Courses</h3>
                            <p className="text-xs opacity-70">View & edit courses</p>
                        </div>
                        <AcademicCapIcon className="h-5 w-5 opacity-50 group-hover:opacity-100 group-hover:scale-110 transition-all" />
                    </Link>
                    
                    <Link to="/admin/quizzes" className="p-3 rounded-lg bg-light-bg dark:bg-dark-bg hover:bg-light-border dark:hover:bg-dark-border transition-colors flex items-center justify-between group">
                        <div>
                            <h3 className="font-semibold text-sm mb-0.5">Quizzes</h3>
                            <p className="text-xs opacity-70">Manage quizzes</p>
                        </div>
                        <ClipboardDocumentCheckIcon className="h-5 w-5 opacity-50 group-hover:opacity-100 group-hover:scale-110 transition-all" />
                    </Link>
                    
                    <Link to="/admin/users" className="p-3 rounded-lg bg-light-bg dark:bg-dark-bg hover:bg-light-border dark:hover:bg-dark-border transition-colors flex items-center justify-between group">
                        <div>
                            <h3 className="font-semibold text-sm mb-0.5">Users</h3>
                            <p className="text-xs opacity-70">Manage user roles</p>
                        </div>
                        <UserGroupIcon className="h-5 w-5 opacity-50 group-hover:opacity-100 group-hover:scale-110 transition-all" />
                    </Link>
                    
                    <Link to="/editor/articles" className="p-3 rounded-lg bg-light-bg dark:bg-dark-bg hover:bg-light-border dark:hover:bg-dark-border transition-colors flex items-center justify-between group">
                        <div>
                            <h3 className="font-semibold text-sm mb-0.5">Content</h3>
                            <p className="text-xs opacity-70">Articles & news</p>
                        </div>
                        <ChartBarIcon className="h-5 w-5 opacity-50 group-hover:opacity-100 group-hover:scale-110 transition-all" />
                    </Link>
                    
                    <Link to="/admin/contacts" className="p-3 rounded-lg bg-light-bg dark:bg-dark-bg hover:bg-light-border dark:hover:bg-dark-border transition-colors flex items-center justify-between group relative">
                        <div>
                            <h3 className="font-semibold text-sm mb-0.5">Messages</h3>
                            <p className="text-xs opacity-70">User inquiries</p>
                        </div>
                        <div className="relative">
                            <EnvelopeIcon className="h-5 w-5 opacity-50 group-hover:opacity-100 group-hover:scale-110 transition-all" />
                            {stats.unreadContacts > 0 && (
                                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center animate-pulse">
                                    {stats.unreadContacts}
                                </span>
                            )}
                        </div>
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboardPage;